import { useState } from "react";
import { Button, Divider, Group, Stack, Text, Title } from "@mantine/core";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { getCurrentUser } from "aws-amplify/auth";
import { GraphQLQuery, generateClient } from "aws-amplify/api";
import { COLORS } from "@/utils/GlobalStyles";
import { useUserProvider } from "@/utils/UserProvider";
import { appShowNotification } from "@/utils/utils";
import { updateUser } from "@/graphql/mutations";
import { UpdateUserInput, UpdateUserMutation } from "@/API";

const client = generateClient();

enum SubscriptionLevel {
  Free = "free",
  Essential = "essential",
  Premium = "premium",
}

type RetentionOption = {
  value: number;
  label: string;
  disabled?: boolean;
};

interface AudioRetentionModalProps {}

const AudioRetentionModal: React.FC<AudioRetentionModalProps> = ({}) => {
  const userSelector = (context: any) => [context.user];
  const { revcatUser, amplifyUser } = useUserProvider();
  const { user } = useAuthenticator(userSelector);
  const [loading, setLoading] = useState(false);
  const [selectedRetention, setSelectedRetention] = useState<number | null>(
    amplifyUser.audioRetentionPeriod ?? 0,
  );

  const onSave = async (retentionPeriod: number) => {
    setLoading(true);
    try {
      const updatedUser: UpdateUserInput = {
        id: user.username,
        audioRetentionPeriod: retentionPeriod,
        _version: amplifyUser._version,
      };
      await client.graphql<GraphQLQuery<UpdateUserMutation>>({
        query: updateUser,
        variables: { input: updatedUser },
      });
      await getCurrentUser();
      appShowNotification({
        message: "Audio retention updated successfully",
        title: "Success",
        variant: "success",
        type: "success",
      });
    } catch (error) {
      console.error("Error updating user:", error);
      appShowNotification({
        message: "Error saving audio retention, please try again",
        title: "Error",
        variant: "danger",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getRetentionOptions = (level: SubscriptionLevel): RetentionOption[] => {
    switch (level) {
      case SubscriptionLevel.Free:
        return [
          { value: 0, label: "0 days" },
          { value: 7, label: "7 days" },
        ];
      case SubscriptionLevel.Essential:
        return [
          { value: 0, label: "0 days" },
          { value: 7, label: "7 days" },
        ];
      case SubscriptionLevel.Premium:
        return [
          { value: 0, label: "0 days" },
          { value: 7, label: "7 days" },
        ];
      default:
        return [];
    }
  };

  const handleSave = async () => {
    if (selectedRetention !== null) {
      await onSave(selectedRetention);
    }
  };

  const handleRetentionSelect = (value: number) => {
    const selectedOption = retentionOptions.find(
      (option) => option.value === value,
    );
    if (!selectedOption?.disabled) {
      setSelectedRetention(value);
    }
  };

  let subscriptionLevel = SubscriptionLevel.Free;
  if (revcatUser?.premium) {
    subscriptionLevel = SubscriptionLevel.Premium;
  } else if (revcatUser?.essential) {
    subscriptionLevel = SubscriptionLevel.Essential;
  }

  const retentionOptions = getRetentionOptions(subscriptionLevel);

  return (
    <Stack gap={0} p="md">
      <Title order={3}>Audio Retention</Title>
      <Text size="sm" c="dimmed">
        Set your preferred retention period for the audio files from your
        recordings. "0 days" means they are deleted immediately after
        processing, "7 days" means they are stored in Manta Classic for 7 days after
        processing, etc.
      </Text>
      <Text size="sm" c="dimmed">
        This only affects your audio files - transcriptions and case data are
        stored as long as you need!
      </Text>
      <Group gap="md" mt="md">
        {retentionOptions.map((option) => (
          <Button
            key={option.value}
            variant={selectedRetention === option.value ? "filled" : "outline"}
            disabled={option.disabled}
            onClick={() => handleRetentionSelect(option.value)}
            color={selectedRetention === option.value ? COLORS.primary : "gray"}
          >
            {option.label}
          </Button>
        ))}
      </Group>
      <Divider mt="lg" />
      <Group mt="lg">
        <Button
          loading={loading}
          disabled={loading}
          w="200px"
          h="50px"
          fw={500}
          radius={30}
          onClick={handleSave}
        >
          Save
        </Button>
      </Group>
    </Stack>
  );
};

export default AudioRetentionModal;
